import { ScreenBase } from "@frui.ts/screens";
import { bound } from "@frui.ts/helpers/dist";
import LocalizationService from "../../services/localizationService";
import { action, computed, observable, reaction } from "mobx";
import { interfaces } from "inversify";
import PriceService from "../../services/priceService";
import { PaymentType } from "../../models/payment";
import PaymentService from "../../services/payment/paymentService";
import GooglePayMetadata from "../../models/googlePayMetadata";
import EventBus, {
  getGooglePayMetadataFailure,
  getGooglePayMetadataSuccess,
  googlePayPaymentBrowserFingerprintLoaded,
  multiEshopPaymentUpdate,
  submitGooglePayPaymentBrowserFingerprint,
  submitGooglePayPaymentFailure,
} from "../../services/eventBus";
import { ErrorMessage } from "../common/types";
import ChargeOrderService from "services/chargeOrderService";
import ChargeOrder from "models/chargeOrder";
import React from "react";
import TurnstileViewModel from "viewModels/common/turnstileViewModel";

export default class SummaryMultiEshopViewModel extends ScreenBase {
  private chargeRegex = /%charge%/g;
  private priceRegex = /%price%/g;
  private unsubscriptions: Array<() => void> = [];

  @observable googlePayMetadata: GooglePayMetadata | undefined;
  @observable googlePayPaymentFingerprint?: string;
  @observable googlePayFingerprintProcessed = false;
  @observable showGooglePaymentButton?: boolean;
  @observable turnstileViewModel: TurnstileViewModel;

  tg = (code: string) => this.localizationService.translateGeneral(`multiEshop.${code}`);
  tp = (price: number) => this.localizationService.formatPrice(price);

  constructor(
    public isFinal: boolean,
    public disabledButton: boolean,
    public localizationService: LocalizationService,
    private chargeOrderService: ChargeOrderService,
    private paymentService: PaymentService,
    private eventBus: EventBus,
    private priceService: PriceService,
    private turnstileViewModelFactory: ReturnType<typeof TurnstileViewModel.Factory>,
    public onContinue?: (errorMessage: ErrorMessage, paymentData?: google.payments.api.PaymentData) => void,
    public onBack?: () => void,
    private statusOrder?: ChargeOrder
  ) {
    super();
    if (isFinal) {
      this.turnstileViewModel = this.turnstileViewModelFactory("multiEshop", false);
    }
    if (isFinal) {
      this.unsubscriptions.push(
        reaction(
          () => this.chargeOrder.paymentType,
          async paymentType => {
            await this.getGooglePayMetadata(paymentType);
          }
        ),
        this.eventBus.subscribe(getGooglePayMetadataSuccess, this.receivedGooglePayMetadata),
        this.eventBus.subscribe(getGooglePayMetadataFailure, this.googlePayMetadataFetchFailure),
        this.eventBus.subscribe(submitGooglePayPaymentFailure, this.googlePayPaymentFailure),
        this.eventBus.subscribe(submitGooglePayPaymentBrowserFingerprint, this.googlePayPaymentBrowserFingerprint),
        this.eventBus.subscribe(multiEshopPaymentUpdate, this.onPaymentDataUpdated)
      );

      this.getGooglePayMetadata(this.chargeOrder.paymentType);
    }
  }

  @action.bound
  onPaymentDataUpdated(data: ReturnType<typeof multiEshopPaymentUpdate>) {
    if (data.payload.paymentType === PaymentType.GooglePay && data.payload.termsAgreement) {
      this.showGooglePaymentButton = true;
      return;
    }
    this.showGooglePaymentButton = false;
  }

  @bound
  protected async getGooglePayMetadata(paymentType: PaymentType | undefined) {
    if (paymentType === PaymentType.GooglePay) {
      await this.paymentService.getGooglePayMetadata();
    }
  }

  @computed
  get isGooglePayMetadataReady() {
    return !this.paymentService.isFetchingGooglePayMetadata && this.googlePayMetadata !== undefined;
  }

  @bound
  protected receivedGooglePayMetadata(data: { type: string; payload: { googlePayMetadata: GooglePayMetadata } }) {
    this.googlePayMetadata = data.payload.googlePayMetadata;
  }

  @bound
  protected googlePayMetadataFetchFailure(data: { type: string; payload: void }) {
    this.googlePayMetadata = undefined;
  }

  @action.bound
  protected googlePayPaymentBrowserFingerprint(googlePayPaymentBrowserFingerprint: { payload: { url: string } }) {
    this.googlePayPaymentFingerprint = googlePayPaymentBrowserFingerprint.payload.url;
  }

  @action.bound
  protected googlePayPaymentFailure() {
    this.googlePayPaymentFingerprint = undefined;
    this.googlePayFingerprintProcessed = false;
  }

  @action.bound
  onGooglePayPaymentFingerprintLoaded = () => {
    if (!this.googlePayFingerprintProcessed) {
      this.googlePayFingerprintProcessed = true;
      this.eventBus.publish(googlePayPaymentBrowserFingerprintLoaded());
    }
  };

  @action.bound
  validateOrder() {
    const chargeOrder = this.statusOrder ?? this.chargeOrderService.chargeOrder;
    return this.chargeOrderService.validateMultiOrder() && !!chargeOrder._termsAgreement;
  }

  protected onInitialize(): Promise<any> | void {
    this.unsubscriptions.push(
      reaction(() => [this.chargeOrder._termsAgreement, this.chargeOrder.paymentType], this.updateChargeOrderItem)
    );
    return super.onInitialize();
  }

  protected onDeactivate(isClosing: boolean): Promise<any> | void {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubsribe => unsubsribe());
      this.unsubscriptions = [];
    }
    return super.onDeactivate(isClosing);
  }

  @computed
  get isGooglePayPaymentMethodSelected() {
    return this.chargeOrder.paymentType === PaymentType.GooglePay;
  }

  @computed
  get locale() {
    return this.localizationService.locale;
  }

  @computed get chargeOrder() {
    return this.chargeOrderService.chargeOrder || this.statusOrder;
  }

  @computed get chargeOrderCharges() {
    return this.chargeOrderService.chargeOrder.charges || this.statusOrder?.charges;
  }

  @computed
  get isTransferPayment() {
    return this.chargeOrder.paymentType === PaymentType.Transfer;
  }

  @computed
  get isChecked() {
    return this.chargeOrder._termsAgreement && this.isFinal && this.isTransferPayment;
  }

  @computed
  get buttonTextKey() {
    if (!this.isFinal) {
      return "continue";
    }
    return this.chargeOrder.paymentType === PaymentType.Transfer ? "payment" : "pay";
  }

  @bound
  vehicleCountForChargeType(index: number) {
    const count = this.chargeOrderCharges[index - 1].licensePlates.length;
    return this.chargeOrderService.translateVehicleCount(count, index);
  }

  @bound
  chargeTypeTotal(index: number, chargeTypeId: string, fuelTypeId: string) {
    const count = this.chargeOrderCharges[index - 1].licensePlates.length;
    const price = this.chargeOrderService.chargePrice(chargeTypeId, fuelTypeId) * count;

    return this.tg("chargeTotalPrice").replace(this.priceRegex, this.localizationService.formatPrice(price));
  }

  @bound
  updateChargeOrderItem() {
    this.chargeOrderService.chargeOrder._termsAgreement = this.chargeOrder._termsAgreement;
    this.chargeOrderService.chargeOrder.paymentType = this.chargeOrder.paymentType;

    if (this.isChecked && this.turnstileViewModel.turnstileToken) {
      this.turnstileViewModel.reset();
    }

    this.chargeOrderService.persistChargeOrder();
  }

  @bound
  uniqueCharges(): { chargeTypeId: string; fuelTypeId: string; index: number }[] {
    const unique = (arr: any[], encoder = JSON.stringify, decoder = JSON.parse) =>
      Array.from(new Set(arr.map(item => encoder(item)))).map(item => decoder(item));
    return unique(
      this.chargeOrderCharges
        .flatMap((chargeOrderItem, index) => ({
          chargeTypeId: chargeOrderItem.chargeTypeId,
          fuelTypeId: chargeOrderItem.fuelTypeId,
          index: index + 1,
        }))
        .filter(data => data.chargeTypeId && data.fuelTypeId)
        .map(d => d as { countryId: string; chargeTypeId: string; fuelTypeId: string; index: number })
        .sort(x => x.index)
    );
  }

  @computed
  get grandTotal() {
    return this.tp(this.priceService.grandTotal(this.chargeOrder));
  }

  @computed
  get total() {
    return this.priceService.grandTotal(this.chargeOrder);
  }

  translateAttribute(code: string) {
    return this.localizationService.translateAttribute("payment", code);
  }

  static Factory({ container }: interfaces.Context) {
    return (
      isFinal: boolean,
      disabledButton = false,
      onContinue?: (errorMessage: ErrorMessage, paymentData?: google.payments.api.PaymentData) => void,
      onBack?: () => void,
      chargeOrder?: ChargeOrder
    ) => {
      return new SummaryMultiEshopViewModel(
        isFinal,
        disabledButton,
        container.get(LocalizationService),
        container.get(ChargeOrderService),
        container.get(PaymentService),
        container.get(EventBus),
        container.get(PriceService),
        container.get(TurnstileViewModel.Factory),
        onContinue,
        onBack,
        chargeOrder
      );
    };
  }
}
